/**
 * Editor helpers over a design row. The document itself always comes from the
 * shared default (`createDefaultWalletPassDesign`) and is validated with the
 * shared schema — there is no editor-only copy of either.
 */

import {
  createDefaultWalletPassDesign,
  walletSampleBindings,
} from "@lemmonhq/blogic/wallet-design";
import type {
  WalletPassDesign,
  WalletPassDesignDocument,
  WalletPassKind,
} from "./types";
import { SAMPLE_COIN_BALANCE } from "./types";

/** An unsaved design has no id yet — it exists only in the open editor. */
export const isUnsaved = (design: WalletPassDesign) => !design.id;

/**
 * A design the editor can render but that does not exist server-side yet.
 * `wallet_pass_designs_create` assigns the id, revision and timestamps when
 * the merchant saves, so the placeholders here never reach the API.
 */
export const createDraftDesign = (
  kind: WalletPassKind,
  name: string,
): WalletPassDesign => ({
  id: "",
  business_unit_id: 0,
  kind,
  name,
  document: createDefaultWalletPassDesign(kind),
  asset_urls: {},
  revision: 0,
  program_count: 0,
  created_at: "",
  updated_at: "",
});

/** Shallow-merges top-level document sections. */
export const patchDocument = (
  design: WalletPassDesign,
  patch: Partial<WalletPassDesignDocument>,
): WalletPassDesign => ({
  ...design,
  document: { ...design.document, ...patch },
});

/** A field or link key that does not collide with any in the document. */
export const newContentKey = (
  document: WalletPassDesignDocument,
  prefix: string,
) => {
  const taken = new Set([
    ...document.content.fields.map((field) => field.key),
    ...document.content.links.map((link) => link.key),
  ]);
  let index = 1;
  while (taken.has(`${prefix}-${index}`)) index += 1;
  return `${prefix}-${index}`;
};

/** Removes a field and every placement of it, so the document stays valid. */
export const removeContentField = (
  document: WalletPassDesignDocument,
  key: string,
): WalletPassDesignDocument => {
  const without = (keys: string[]) => keys.filter((k) => k !== key);
  const firstOther = document.content.fields.find((f) => f.key !== key)?.key;
  return {
    ...document,
    content: {
      ...document.content,
      fields: document.content.fields.filter((field) => field.key !== key),
    },
    apple: {
      ...document.apple,
      rows: {
        header: without(document.apple.rows.header),
        primary: without(document.apple.rows.primary),
        secondary: without(document.apple.rows.secondary),
        auxiliary: without(document.apple.rows.auxiliary),
        back: without(document.apple.rows.back),
      },
    },
    google: {
      ...document.google,
      header:
        document.google.header === key
          ? (firstOther ?? document.google.header)
          : document.google.header,
      subheader:
        document.google.subheader === key ? null : document.google.subheader,
      rows: document.google.rows
        .map(without)
        .filter((row) => row.length > 0),
      modules: without(document.google.modules),
    },
    browser: {
      ...document.browser,
      primary: document.browser.primary === key ? null : document.browser.primary,
      secondary: without(document.browser.secondary),
    },
  };
};

/** Binding values the previews resolve against. */
export const previewBindings = (kind: WalletPassKind) => ({
  ...walletSampleBindings(kind),
  ...(kind === "coin" ? { "coins.balance": SAMPLE_COIN_BALANCE } : {}),
});
